/**
 * ProfileEntrepriseFiscalController — onglet « Options fiscales »
 * de /profil-entreprise.html (EPIC-099 / 99.72, comble le gap 7.7).
 *
 * Pilote 3 blocs par source de vérité (§2.F) :
 *  - `block-regime-fiscal` : régime(s) dérivé(s) des activités (lecture
 *    seule) + option versement libératoire éditable (nm-edit-block).
 *  - `block-tva`           : régime TVA + périodicité CA3 (lecture seule,
 *    le choix se fait dans le module TVA → lien vers /tva.html).
 *  - `block-urssaf`        : périodicité + statut du mandat de tierce
 *    déclaration + date de dernière récupération + bouton
 *    « Récupérer la périodicité depuis urssaf.fr ».
 *
 * Conventions CLAUDE.md :
 *   - Pas d'HTML inline en JS : résumés rendus depuis <template> clonés
 *     via cloneTemplate / fillSlots.
 *   - Vocab UI : « PA » (jamais « PDP »/« SuperPDP »), pas d'« OCR ».
 *   - Re-render sans rechargement de page (read-after-write côté API).
 */

import BaseController from "./base_controller.js";
import { api } from "/js/services/api.js";
import { formatDate } from "/js/utils/format.js";

const REGIME_LABELS = {
  bic_vente: "micro-BIC vente",
  bic_services: "micro-BIC prestations",
  bnc: "micro-BNC",
};

const VAT_REGIME_LABELS = {
  franchise: "Franchise en base",
  simplified_ca12: "Réel simplifié (CA12)",
  normal_ca3: "Réel normal (CA3)",
};

const PERIODICITY_LABELS = {
  monthly: "mensuelle",
  quarterly: "trimestrielle",
};

const MANDATE_LABELS = {
  active: "actif",
  not_signed: "non signé",
  revoked: "révoqué",
};

export default class ProfileEntrepriseFiscalController extends BaseController {
  static targets = [
    "blockRegime",
    "regimeSummary",
    "regimeStatic",
    "vlYesInput",
    "vlNoInput",
    "vlSubmit",
    "vlConfirm",
    "vlActions",
    "regimeError",
    "tvaSummary",
    "urssafSummary",
    "urssafRefreshBtn",
  ];

  state = {
    fiscal: null,
  };

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  async connect() {
    await this._refreshFromApi();
    // §99.35 — re-dériver les régimes quand une activité change sans
    // rechargement (l'onglet Activités émet `activities:updated`).
    this._onActivitiesUpdated = () => { this._refreshFromApi(); };
    document.addEventListener("activities:updated", this._onActivitiesUpdated);
  }

  disconnect() {
    if (this._onActivitiesUpdated) {
      document.removeEventListener("activities:updated", this._onActivitiesUpdated);
    }
  }

  async _refreshFromApi() {
    try {
      const data = await api.getOrganizationProfile();
      if (data && data.fiscal) this._hydrate(data.fiscal);
    } catch (error) {
      console.warn("[profile-entreprise-fiscal] hydration failed", error);
    }
  }

  // ---------------------------------------------------------------------------
  // edit-block versement libératoire : open / cancel / submit
  // ---------------------------------------------------------------------------

  openVl(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreVlInputs();
    this._hideError();
    this._hideVlConfirm();
    this._open(block);
  }

  cancelVl(event) {
    event.preventDefault();
    const block = event.currentTarget.closest("[data-edit-block]");
    if (!block) return;
    this._restoreVlInputs();
    this._hideError();
    this._hideVlConfirm();
    this._close(block);
  }

  async submitVl(event) {
    event.preventDefault();
    event.stopImmediatePropagation();

    const answered = (this.hasVlYesInputTarget && this.vlYesInputTarget.checked)
      || (this.hasVlNoInputTarget && this.vlNoInputTarget.checked);
    if (!answered) {
      this._showError("Indiquez si vous optez pour le versement libératoire.");
      return;
    }

    const hasVlOption = this.hasVlYesInputTarget && this.vlYesInputTarget.checked;
    const changed = this.state.fiscal && hasVlOption !== this.state.fiscal.hasVlOption;

    // Confirmation UI (99.76) : un changement de valeur alors que le dernier
    // changement date de moins de 365 j demande une confirmation explicite.
    // Aucune règle serveur — le seuil est purement indicatif côté interface.
    if (changed && this._vlChangedRecently()) {
      this._hideError();
      this._showVlConfirm();
      return;
    }

    await this._persistVl(hasVlOption);
  }

  async confirmVl(event) {
    event.preventDefault();
    const hasVlOption = this.hasVlYesInputTarget && this.vlYesInputTarget.checked;
    await this._persistVl(hasVlOption);
  }

  async _persistVl(hasVlOption) {
    if (this.hasVlSubmitTarget) this.vlSubmitTarget.disabled = true;
    try {
      const data = await api.updateFiscalOptions({ hasVlOption });
      if (data && data.fiscal) this._hydrate(data.fiscal);
      this._hideVlConfirm();
      if (this.hasBlockRegimeTarget) this._close(this.blockRegimeTarget);
      this.showSuccess("Option versement libératoire enregistrée.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Erreur lors de la mise à jour de l'option versement libératoire.";
      this._hideVlConfirm();
      this._showError(msg);
    } finally {
      if (this.hasVlSubmitTarget) this.vlSubmitTarget.disabled = false;
    }
  }

  /**
   * Le dernier changement déclaratif de l'option date-t-il de moins de 365 j ?
   * `false` si aucun changement antérieur (premier toggle → pas de confirmation).
   */
  _vlChangedRecently() {
    const changedAt = this.state.fiscal && this.state.fiscal.vlOptionChangedAt;
    if (!changedAt) return false;
    const then = new Date(changedAt);
    if (Number.isNaN(then.getTime())) return false;
    const days = (Date.now() - then.getTime()) / 86400000;
    return days < 365;
  }

  _showVlConfirm() {
    if (this.hasVlConfirmTarget) this.vlConfirmTarget.hidden = false;
    if (this.hasVlActionsTarget) this.vlActionsTarget.hidden = true;
  }

  _hideVlConfirm() {
    if (this.hasVlConfirmTarget) this.vlConfirmTarget.hidden = true;
    if (this.hasVlActionsTarget) this.vlActionsTarget.hidden = false;
  }

  // ---------------------------------------------------------------------------
  // URSSAF : récupération de la périodicité (API020)
  // ---------------------------------------------------------------------------

  async refreshUrssaf(event) {
    event.preventDefault();
    if (this.hasUrssafRefreshBtnTarget) this.urssafRefreshBtnTarget.disabled = true;
    try {
      const data = await api.refreshUrssafPeriodicity();
      if (data && data.fiscal) this._hydrate(data.fiscal);
      this.showSuccess("Périodicité récupérée depuis l'URSSAF.");
    } catch (error) {
      const msg = error && error.message
        ? error.message
        : "Impossible de récupérer la périodicité depuis l'URSSAF.";
      this.showError(msg);
    } finally {
      if (this.hasUrssafRefreshBtnTarget) this.urssafRefreshBtnTarget.disabled = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Hydration + render
  // ---------------------------------------------------------------------------

  _hydrate(fiscal) {
    this.state.fiscal = {
      regimes: Array.isArray(fiscal.regimes) ? fiscal.regimes : [],
      hasVlOption: fiscal.hasVlOption === true,
      vlOptionChangedAt: fiscal.vlOptionChangedAt ?? null,
      vatRegimeChosen: fiscal.vatRegimeChosen ?? null,
      vatRegimeChosenAt: fiscal.vatRegimeChosenAt ?? null,
      vatCa3Periodicity: fiscal.vatCa3Periodicity ?? null,
      vatSubjectionStatus: fiscal.vatSubjectionStatus ?? null,
      urssafPeriodicity: fiscal.urssafPeriodicity ?? null,
      urssafMandateStatus: fiscal.urssafMandateStatus ?? null,
      urssafSyncedAt: fiscal.urssafSyncedAt ?? null,
    };
    this._renderRegimeSummary();
    this._renderTvaSummary();
    this._renderUrssafSummary();
    this._restoreVlInputs();
  }

  _regimeLabel(regime) {
    return REGIME_LABELS[regime.category] ?? regime.label ?? regime.category;
  }

  _renderRegimeSummary() {
    if (!this.hasRegimeSummaryTarget) return;
    const { regimes, hasVlOption } = this.state.fiscal;

    if (regimes.length === 0) {
      this._replaceChildren(this.regimeSummaryTarget, this.cloneTemplate("tpl-fiscal-regime-empty"));
      if (this.hasRegimeStaticTarget) this.regimeStaticTarget.textContent = "—";
      return;
    }

    const fragment = this.cloneTemplate("tpl-fiscal-regime-summary");
    this.fillSlots(fragment, { vl: hasVlOption ? "oui" : "non" });
    const regimesSlot = fragment.querySelector('[data-slot="regimes"]');
    if (regimesSlot) {
      regimes.forEach((regime, index) => {
        if (index > 0) regimesSlot.appendChild(document.createTextNode(" · "));
        const chunk = this.cloneTemplate("tpl-fiscal-regime-chunk");
        this.fillSlots(chunk, {
          label: this._regimeLabel(regime),
          abattement: String(regime.abattement ?? "—"),
        });
        regimesSlot.appendChild(chunk);
      });
    }
    this._replaceChildren(this.regimeSummaryTarget, fragment);

    if (this.hasRegimeStaticTarget) {
      this.regimeStaticTarget.textContent = regimes
        .map((regime) => this._regimeLabel(regime))
        .join(" · ");
    }
  }

  _renderTvaSummary() {
    if (!this.hasTvaSummaryTarget) return;
    const { vatRegimeChosen, vatRegimeChosenAt, vatCa3Periodicity, vatSubjectionStatus } = this.state.fiscal;

    // Régime retenu si choisi, sinon état d'assujettissement courant.
    const regimeKey = vatRegimeChosen ?? vatSubjectionStatus ?? "franchise";
    const fragment = this.cloneTemplate("tpl-fiscal-tva-summary");
    this.fillSlots(fragment, {
      regime: VAT_REGIME_LABELS[regimeKey] ?? "Franchise en base",
      ca3: PERIODICITY_LABELS[vatCa3Periodicity] ?? "—",
      chosenAt: formatDate(vatRegimeChosenAt, { emptyValue: "—" }),
    });

    const ca3Chunk = fragment.querySelector('[data-summary-chunk="ca3"]');
    if (ca3Chunk) ca3Chunk.hidden = !(regimeKey === "normal_ca3" && vatCa3Periodicity);
    const chosenChunk = fragment.querySelector('[data-summary-chunk="chosenAt"]');
    if (chosenChunk) chosenChunk.hidden = !vatRegimeChosenAt;

    this._replaceChildren(this.tvaSummaryTarget, fragment);
  }

  _renderUrssafSummary() {
    if (!this.hasUrssafSummaryTarget) return;
    const { urssafPeriodicity, urssafMandateStatus, urssafSyncedAt } = this.state.fiscal;

    if (!urssafPeriodicity) {
      this._replaceChildren(
        this.urssafSummaryTarget,
        this.cloneTemplate("tpl-fiscal-urssaf-summary-empty"),
      );
      return;
    }

    const fragment = this.cloneTemplate("tpl-fiscal-urssaf-summary");
    this.fillSlots(fragment, {
      periodicity: PERIODICITY_LABELS[urssafPeriodicity] ?? "—",
      mandate: MANDATE_LABELS[urssafMandateStatus] ?? "non signé",
      syncedAt: formatDate(urssafSyncedAt, { includeTime: true, emptyValue: "—" }),
    });
    this._replaceChildren(this.urssafSummaryTarget, fragment);
  }

  _restoreVlInputs() {
    const hasVlOption = this.state.fiscal ? this.state.fiscal.hasVlOption : false;
    if (this.hasVlYesInputTarget) this.vlYesInputTarget.checked = hasVlOption === true;
    if (this.hasVlNoInputTarget) this.vlNoInputTarget.checked = hasVlOption !== true;
  }

  // ---------------------------------------------------------------------------
  // Helpers (edit-block open/close + erreurs)
  // ---------------------------------------------------------------------------

  _showError(message) {
    if (!this.hasRegimeErrorTarget) {
      this.showError(message);
      return;
    }
    this.regimeErrorTarget.textContent = message;
    this.regimeErrorTarget.hidden = false;
  }

  _hideError() {
    if (!this.hasRegimeErrorTarget) return;
    this.regimeErrorTarget.textContent = "";
    this.regimeErrorTarget.hidden = true;
  }

  _open(block) {
    block.dataset.editState = "editing";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (summary) summary.hidden = true;
    if (form) {
      form.hidden = false;
      const first = form.querySelector("input:not([type='hidden']), select, textarea");
      if (first) first.focus();
    }
  }

  _close(block) {
    block.dataset.editState = "idle";
    const summary = block.querySelector("[data-edit-summary]");
    const form = block.querySelector("[data-edit-form]");
    if (form) form.hidden = true;
    if (summary) summary.hidden = false;
  }

  _replaceChildren(target, fragment) {
    while (target.firstChild) target.removeChild(target.firstChild);
    target.appendChild(fragment);
  }
}
